import { inject, Injectable } from '@angular/core';
import { map, Observable, tap } from 'rxjs';
import {
  ModelFacadeStrategy,
  ModelMapperStrategy,
  ModelsServiceStrategy,
  ProjectFacadeStrategy,
  RecentProjectsService,
} from 'shared';
import { CreateModelParams, Model } from '@web-portal/domains/shared/domain-models';
import { ModelEventBus } from '@web-portal/domains/shared/event-bus';
import { ModelsAdapterService } from '@web-portal/domains/models/data-access';

@Injectable()
export class ModelsService extends ModelsServiceStrategy {
  private readonly modelsAdapter = inject(ModelsAdapterService);
  private readonly projectFacade = inject(ProjectFacadeStrategy);
  private readonly modelFacade = inject(ModelFacadeStrategy);
  private readonly recentProjectsService = inject(RecentProjectsService);
  private readonly modelMapper = inject(ModelMapperStrategy);
  private readonly modelEventBus = inject(ModelEventBus);

  getModelsForProject(projectId: string): Observable<Model[]> {
    return this.modelsAdapter.getModels(projectId).pipe(
      map(models => models.map(model => this.modelMapper.mapToModel(model))),
      tap({
        next: models => {
          this.modelEventBus.emit({ type: '[Model] updated', payload: models });
        },
      })
    );
  }

  refreshModels(): Observable<Model[]> | null {
    const project = this.projectFacade.selectedProject();
    if (!project?.id) {
      return null;
    }
    return this.getModelsForProject(project.id);
  }

  createModel(params: CreateModelParams): Observable<Model> {
    return this.modelsAdapter.createModel(params).pipe(
      map(model => this.modelMapper.mapToModel(model)),
      tap({
        next: model => {
          this.modelEventBus.emit({ type: '[Model] created', payload: model });
          this.updateCachedModelsCount();
        },
      })
    );
  }

  deleteModel(modelId: string): Observable<unknown> {
    return this.modelsAdapter.deleteModel(modelId).pipe(
      tap({
        next: () => {
          this.modelEventBus.emit({ type: '[Model] deleted', payload: modelId });
          this.updateCachedModelsCount();
        },
      })
    );
  }

  getThumbnailUrl(modelId: string): string {
    return this.modelsAdapter.getThumbnailUrl(modelId);
  }

  getDownloadUrl(modelId: string, revision: number): string {
    return this.modelsAdapter.getDownloadUrl(modelId, revision);
  }

  renameModel(modelId: string, name: string): Observable<unknown> {
    return this.modelsAdapter.renameModel(modelId, name);
  }

  /** Keeps the recent projects' cached model count in sync with the current model list. */
  private updateCachedModelsCount(): void {
    const projectId = this.projectFacade.selectedProject()?.id;
    if (projectId) {
      this.recentProjectsService.updateCachedModelsCount(
        projectId,
        this.modelFacade.models().length
      );
    }
  }
}
